/**
 * In-memory data store for browser MVP
 * Persists to localStorage for demo purposes
 * Enhanced with Team & Roles support
 */

import type {
  Workspace,
  Note,
  Task,
  Contact,
  Insight,
  AssistantSuggestion,
  TeamMember,
  BuildRun,
  QAItem,
  ReviewItem,
  ROIDiagnosis,
} from './types';

export interface Store {
  workspaces: Map<string, Workspace>;
  notes: Map<string, Note>;
  tasks: Map<string, Task>;
  contacts: Map<string, Contact>;
  insights: Map<string, Insight>;
  suggestions: Map<string, AssistantSuggestion>;
  teamMembers: Map<string, TeamMember>;
  buildRuns: Map<string, BuildRun>;
  qaItems: Map<string, QAItem>;
  reviewItems: Map<string, ReviewItem>;
  roiDiagnoses: Map<string, ROIDiagnosis>;
}

const STORAGE_KEY = 'business-ai-store-v2';

function deserializeDates<T extends object>(obj: T): T {
  const result = { ...(obj as object) } as Record<string, unknown>;
  for (const key of Object.keys(result)) {
    const value = result[key];
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
      result[key] = new Date(value);
    }
  }
  return result as T;
}

function loadFromStorage(): Store {
  const store: Store = {
    workspaces: new Map(),
    notes: new Map(),
    tasks: new Map(),
    contacts: new Map(),
    insights: new Map(),
    suggestions: new Map(),
    teamMembers: new Map(),
    buildRuns: new Map(),
    qaItems: new Map(),
    reviewItems: new Map(),
    roiDiagnoses: new Map(),
  };

  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      for (const [key, value] of Object.entries(parsed.workspaces || {})) {
        store.workspaces.set(key, deserializeDates(value as Workspace));
      }
      for (const [key, value] of Object.entries(parsed.notes || {})) {
        store.notes.set(key, deserializeDates(value as Note));
      }
      for (const [key, value] of Object.entries(parsed.tasks || {})) {
        store.tasks.set(key, deserializeDates(value as Task));
      }
      for (const [key, value] of Object.entries(parsed.contacts || {})) {
        store.contacts.set(key, deserializeDates(value as Contact));
      }
      for (const [key, value] of Object.entries(parsed.insights || {})) {
        store.insights.set(key, deserializeDates(value as Insight));
      }
      for (const [key, value] of Object.entries(parsed.suggestions || {})) {
        store.suggestions.set(key, deserializeDates(value as AssistantSuggestion));
      }
      for (const [key, value] of Object.entries(parsed.teamMembers || {})) {
        store.teamMembers.set(key, deserializeDates(value as TeamMember));
      }
      for (const [key, value] of Object.entries(parsed.buildRuns || {})) {
        store.buildRuns.set(key, deserializeDates(value as BuildRun));
      }
      for (const [key, value] of Object.entries(parsed.qaItems || {})) {
        store.qaItems.set(key, deserializeDates(value as QAItem));
      }
      for (const [key, value] of Object.entries(parsed.reviewItems || {})) {
        store.reviewItems.set(key, deserializeDates(value as ReviewItem));
      }
      for (const [key, value] of Object.entries(parsed.roiDiagnoses || {})) {
        store.roiDiagnoses.set(key, deserializeDates(value as ROIDiagnosis));
      }
    }
  } catch {
    console.warn('Failed to load from localStorage');
  }

  return store;
}

function saveToStorage(store: Store): void {
  try {
    const serialized = {
      workspaces: Object.fromEntries(store.workspaces),
      notes: Object.fromEntries(store.notes),
      tasks: Object.fromEntries(store.tasks),
      contacts: Object.fromEntries(store.contacts),
      insights: Object.fromEntries(store.insights),
      suggestions: Object.fromEntries(store.suggestions),
      teamMembers: Object.fromEntries(store.teamMembers),
      buildRuns: Object.fromEntries(store.buildRuns),
      qaItems: Object.fromEntries(store.qaItems),
      reviewItems: Object.fromEntries(store.reviewItems),
      roiDiagnoses: Object.fromEntries(store.roiDiagnoses),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(serialized));
  } catch {
    console.warn('Failed to save to localStorage');
  }
}

let store: Store = loadFromStorage();

export function getStore(): Store {
  return store;
}

export function persistStore(): void {
  saveToStorage(store);
}

export function clearStore(): void {
  store.workspaces.clear();
  store.notes.clear();
  store.tasks.clear();
  store.contacts.clear();
  store.insights.clear();
  store.suggestions.clear();
  store.teamMembers.clear();
  store.buildRuns.clear();
  store.qaItems.clear();
  store.reviewItems.clear();
  store.roiDiagnoses.clear();
  localStorage.removeItem(STORAGE_KEY);
}

export function addEntity<T extends { id: string }>(
  map: Map<string, T>,
  entity: T
): T {
  map.set(entity.id, entity);
  persistStore();
  return entity;
}

export function getEntity<T>(map: Map<string, T>, id: string): T | undefined {
  return map.get(id);
}

export function getAllEntities<T>(map: Map<string, T>): T[] {
  return Array.from(map.values());
}

export function getEntitiesByWorkspace<T extends { workspaceId: string }>(
  map: Map<string, T>,
  workspaceId: string
): T[] {
  return Array.from(map.values()).filter((e) => e.workspaceId === workspaceId);
}

export function updateEntity<T extends { id: string; updatedAt: Date }>(
  map: Map<string, T>,
  id: string,
  updates: Partial<Omit<T, 'id' | 'updatedAt'>>
): T | undefined {
  const entity = map.get(id);
  if (!entity) return undefined;
  const updated = { ...entity, ...updates, updatedAt: new Date() } as T;
  map.set(id, updated);
  persistStore();
  return updated;
}

export function deleteEntity<T>(map: Map<string, T>, id: string): boolean {
  const result = map.delete(id);
  if (result) persistStore();
  return result;
}

// ROI Diagnosis helpers
export function saveROIDiagnosis(diagnosis: ROIDiagnosis): ROIDiagnosis {
  store.roiDiagnoses.set(diagnosis.workspaceId, diagnosis);
  persistStore();
  return diagnosis;
}

export function getROIDiagnosis(workspaceId: string): ROIDiagnosis | undefined {
  return store.roiDiagnoses.get(workspaceId);
}
